import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { addEntities, removeAllEntities, setAllEntities, withEntities } from '@ngrx/signals/entities';
import { IrisDMSFileI } from '../models/IrisDMSFile';
import { IrisDmsContentPageChangedEventI, IrisDmsContentPaginationI } from '../models/IrisDmsContentPagination';
import { IrisQueryParamsOrderBy } from '@iris/api-query';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { withRequestState, setLoading, setLoaded, setError, setInit } from '@iris/common/signals/features/request-status.feature';
import { debounceTime, filter, map, pipe, switchMap } from 'rxjs';
import { computed, inject, Signal } from '@angular/core';
import { IrisFoldersService } from '@iris/common/services/docs/folders.service';
import { tapResponse } from '@ngrx/operators';
import { isNumber } from 'lodash';
import { HttpErrorResponse } from '@angular/common/http';
import { IrisDmsFilterService } from '../services/dms-filter.service';
import { IrisDmsFoldersSandbox } from '../sandbox/dms-folders.sandbox';
import { IrisContentViewType } from '../models/IrisDmsViewType';
import { DmsFilesGridColName } from '../models/IrisDmsGridTypes';

export interface IrisDmsMNodalFilesStateI {
  pagination: IrisDmsContentPaginationI;
  total: number;
  viewType: IrisContentViewType;
};

const tileViewPageSize = 24;
const listViewPageSize = 25;

const defaultPagination: IrisDmsContentPaginationI = {
  offset: 0,
  limit: listViewPageSize,
  orderBy: [new IrisQueryParamsOrderBy({ name: DmsFilesGridColName.UpdatedOn, value: 'desc' })],
};

const defaultState: IrisDmsMNodalFilesStateI = {
  pagination: defaultPagination,
  total: 0,
  viewType: IrisContentViewType.List,
};

export const IrisDmsModalFilesStore = signalStore(
  { providedIn: 'root', protectedState: false },
  withEntities<IrisDMSFileI>(),
  withRequestState(),
  withState<IrisDmsMNodalFilesStateI>(defaultState),
  withComputed((store) => ({
    files: store.entities,
    orderBy: computed(() => store.pagination().orderBy),
    offset: computed(() => store.pagination().offset),
    limit: computed(() => store.pagination().limit),
    filter: computed(() => store.pagination().filter),
    hasNextPage: computed(() => {
      const { offset, limit } = store.pagination();
      return offset + limit < store.total();
    }),
    pageNumber: computed(() => {
      const { offset, limit } = store.pagination();
      return offset / limit || 0;
    }),
    isTileVIew: computed(() => store.viewType() === IrisContentViewType.Tile),
  })),
  withComputed(store => ({
    pageSize: computed(() => store.isTileVIew() ? tileViewPageSize : listViewPageSize),
  })),
  withMethods((
    store,
    foldersService = inject(IrisFoldersService),
    dmsFilterService = inject(IrisDmsFilterService),
    dmsFoldersSandbox = inject(IrisDmsFoldersSandbox),
  ) => ({
    getFileById: (fileId: string): Signal<IrisDMSFileI> => computed(() => store.entityMap()[fileId]),
    setViewType: (isTileView = false) => {
      const viewType = isTileView ? IrisContentViewType.Tile : IrisContentViewType.List;
      patchState(store, { viewType, pagination: { limit: store.pageSize() } });
    },
    addFiles: (files: IrisDMSFileI[]) => patchState(store, addEntities(files)),
    fetchFiles$: rxMethod<IrisDmsContentPaginationI>(
      pipe(
        debounceTime(100),
        filter((pagination) => {
          const selectedFolderId = dmsFoldersSandbox.selectedFolderIdModal();
          return !store.loading()
            && !!selectedFolderId
            && (!store.total() || pagination.offset <= store.total());
        }),
        switchMap((pagination) => {
          const selectedFolderId = dmsFoldersSandbox.selectedFolderIdModal();
          const { offset, limit, orderBy, filter } = pagination;
          const firstPageLoading = offset === 0;
          if (firstPageLoading) {
            patchState(store, setLoading());
          }
          return foldersService.getContentPage({
            folderId: selectedFolderId,
            offset,
            limit,
            orderBy,
            excludeHistory: true,
            onlyFiles: true,
            filter: dmsFilterService.extractModalPageFilter(filter),
          }).pipe(
            tapResponse({
              next: ({ files, count }) => {
                const viewType = store.viewType();
                const actionToPatch = !firstPageLoading && viewType === IrisContentViewType.Tile
                  ? addEntities(files)
                  : setAllEntities(files);
                const stateUpdater: Partial<IrisDmsMNodalFilesStateI> = { pagination };
                if (isNumber(count)) {
                  stateUpdater.total = count;
                }
                patchState(store, actionToPatch, stateUpdater, setLoaded());
              },
              error: (err: HttpErrorResponse) => patchState(store, setError(err.message)),
            }),
          );
        }),
      ),
    ),
  })),
  withMethods(store => ({
    requestFiles$: rxMethod<IrisDmsContentPageChangedEventI>(
      pipe(
        debounceTime(100),
        map(changedPagination => ({
          offset: changedPagination.page * changedPagination.pageSize,
          limit: changedPagination.pageSize,
          orderBy: changedPagination.orderBy,
          filter: changedPagination.filter,
        })),
        map(pagination => store.fetchFiles$(pagination)),
      ),
    ),
    resetPagination: () => {
      const stateChanges = {
        pagination: { ...defaultPagination, limit: store.pageSize() },
        total: null,
      };
      patchState(store, removeAllEntities(), stateChanges);
      store.fetchFiles$(stateChanges.pagination);
    },
    resetFiles: () => {
      patchState(store, removeAllEntities(), defaultState, setInit());
    },
  })),
);
